import { FC, useEffect, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { client, MarketCatalogue } from '../../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../../components/forms/Pagination';
import { Section } from '../../../../components/ui/Section';
import CataloguesList from './CataloguesList';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

type Props = {
  onCreateNew: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilterChange?: (_instance: string, _market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
};

const CataloguesTab: FC<Props> = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm }) => {
  const { t } = useTranslation('catalogues');
  const [list, setList] = useState<MarketCatalogue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const { instances, getMarkets, ensureMarketsLoaded, isMarketsLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const pageSize = 20;

  const fetchCatalogues = async () => {
    setIsLoading(true);
    try {
      const result = await client.getMarketCatalogues({
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching catalogues:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (isMarketsLoading(selectedInstance)) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
        return;
      }
    }

    if (selectedInstance || selectedInstance === 'all') {
      fetchCatalogues();
    } else {
      setList([]);
      setTotalCount(0);
    }
  }, [markets, currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket(''); // Reset market when instance changes
    setCurrentPage(0);
    onFilterChange?.(event.target.value, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, event.target.value);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('all', '', '');
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('management.search')}
          placeholder={t('management.searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('management.clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('management.search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByInstance')}
          </InputLabel>
          <Select
            value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('management.filterByInstance')}
            displayEmpty
            notched
            renderValue={(value) => {
              if (value === '') {
                return <span>{t('management.allInstances')}</span>;
              }

              return instances?.find(i => i.id.toString() === value)?.name || '';
            }}
          >
            <MenuItem value="all">
              {t('management.allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem
                key={instance.id}
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByMarket')}
          </InputLabel>
          <Select
            value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
            onChange={handleMarketChange}
            label={t('management.filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('management.allMarkets')}
            </MenuItem>
            {markets.map((market) => (
              <MenuItem
                key={market.customerTypeId}
                value={market.customerTypeId.toString()}
              >
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <CataloguesList
            list={list}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default CataloguesTab;
